import type { AuthTenant, AuthUser } from '@/lib/auth';

// Generalized vertical-operator access check (VERTICAL-PRICING-ADMIN-1).
// Replaces the Sensarra-hardcoded `sensarraAccess` with a vertical-parametrized
// gate: a server-confirmed Director administers every vertical; a vertical
// operator administers only the vertical their tenant is an operator of. The
// authoritative gate is still backend-side — this is the UX pre-check that keeps
// non-operators out of the management center.

export interface VerticalAdminAccess {
  canView: boolean;
  reason: string;
}

const OPERATOR_ROLES = new Set(['admin', 'owner', 'operator', 'vertical_operator']);

function isServerConfirmedDirector(user: AuthUser | null | undefined): boolean {
  return user?.isDirector === true && user.directorStatusSource === 'auth_me';
}

function tenantMatchesVertical(tenant: AuthTenant | null | undefined, vertical: string): boolean {
  const v = vertical.toLowerCase();
  const key = tenant?.tenant_id?.toLowerCase() ?? '';
  const label = tenant?.display_name?.toLowerCase() ?? '';
  return key === v || key.startsWith(`${v}-`) || label.includes(v);
}

function isOperatorTenant(tenant: AuthTenant | null | undefined, vertical: string): boolean {
  const role = tenant?.role?.toLowerCase() ?? '';
  const operatorRoles = new Set([...OPERATOR_ROLES, `${vertical.toLowerCase()}_operator`]);
  return tenantMatchesVertical(tenant, vertical) && operatorRoles.has(role);
}

export function getVerticalAdminAccess(
  user: AuthUser | null | undefined,
  vertical: string,
): VerticalAdminAccess {
  if (isServerConfirmedDirector(user)) {
    return { canView: true, reason: 'Director access confirmed by /auth/me.' };
  }
  const tenants = [user?.tenant, ...(user?.tenants ?? [])];
  if (tenants.some((t) => isOperatorTenant(t, vertical))) {
    return { canView: true, reason: `${vertical} operator role recognized.` };
  }
  return { canView: false, reason: `${vertical} operator access required.` };
}
